import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X, CheckCircle2, Clock, ChevronDown, ArrowRight } from 'lucide-react';

const AutocompleteInput = ({ value, onChange, placeholder, suggestions, label, required }) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState(suggestions);

    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        onChange(e);
        if (inputValue.trim() === '') {
            setFilteredSuggestions(suggestions);
        } else {
            const filtered = suggestions.filter(s => 
                s.toLowerCase().includes(inputValue.toLowerCase())
            );
            setFilteredSuggestions(filtered);
        }
    };

    const handleFocus = () => {
        setShowSuggestions(true);
        setFilteredSuggestions(suggestions); // Show all suggestions on focus
    };

    return (
        <div className="relative">
            <label className="block text-sm font-bold text-gray-900 mb-3">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
                <input
                    type="text"
                    value={value}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder={placeholder}
                    className="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base"
                />
                <ChevronDown 
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" 
                    size={18} 
                />
            </div>
            {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-2 bg-white border-2 border-blue-200 rounded-xl shadow-2xl max-h-48 overflow-y-auto">
                    {filteredSuggestions.map((suggestion, idx) => (
                        <button
                            key={idx}
                            type="button"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                onChange({ target: { value: suggestion } });
                                setShowSuggestions(false);
                            }}
                            className="w-full px-4 py-2.5 text-left hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-700 font-medium first:rounded-t-xl last:rounded-b-xl text-sm"
                        >
                            {suggestion}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const ProfileRequiredStep = ({ profileData, setProfileData, onNext, onSkip }) => {
    const [skillInput, setSkillInput] = useState('');

    const addSkill = () => {
        if (skillInput.trim() && !profileData.skills.includes(skillInput.trim())) {
            setProfileData({
                ...profileData,
                skills: [...profileData.skills, skillInput.trim()]
            });
            setSkillInput('');
        }
    };

    const removeSkill = (skill) => {
        setProfileData({
            ...profileData,
            skills: profileData.skills.filter(s => s !== skill)
        });
    };

    const formContent = (
        <div className="max-w-6xl mx-auto">
            {/* Header - minimalistic */}
            <div className="mb-5">
                <p className="font-semibold text-gray-900 text-lg mb-1">Required Information</p>
                <p className="text-gray-600 text-sm">
                    Help us match you with the most relevant opportunities
                </p>
            </div>

            {/* Two-column layout for compact display */}
            <div className="grid lg:grid-cols-2 gap-5">
                {/* Left Column */}
                <div className="space-y-5">
            {/* Experience Level */}
            <div>
                        <label className="block text-sm font-bold text-gray-900 mb-3">
                    Experience Level <span className="text-red-500">*</span>
                </label>
                        <div className="grid grid-cols-2 gap-3">
                    {[
                        { value: 'entry', label: 'Entry Level', years: '0-2 years' },
                        { value: 'mid', label: 'Mid Level', years: '3-5 years' },
                        { value: 'senior', label: 'Senior', years: '6-10 years' },
                        { value: 'lead', label: 'Lead/Principal', years: '10+ years' }
                    ].map((level) => (
                        <button
                            key={level.value}
                            type="button"
                            onClick={() => setProfileData({ ...profileData, experienceLevel: level.value })}
                                    className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                                profileData.experienceLevel === level.value
                                            ? 'border-blue-600 bg-blue-50 shadow-md shadow-blue-200'
                                    : 'border-gray-200 hover:border-blue-300 bg-white hover:shadow-md'
                            }`}
                        >
                                    <div className="font-bold text-gray-900 text-base mb-1">{level.label}</div>
                                    <div className="text-xs text-gray-600">{level.years}</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Desired Role */}
                    <div>
            <AutocompleteInput
                value={profileData.desiredRole}
                onChange={(e) => setProfileData({ ...profileData, desiredRole: e.target.value })}
                            placeholder="e.g., Software Engineer"
                suggestions={[
                    'Software Engineer',
                    'Senior Software Engineer',
                    'Staff Software Engineer',
                    'Principal Engineer',
                    'Engineering Manager',
                    'Product Manager',
                    'Senior Product Manager',
                    'Product Designer',
                    'UX Designer',
                    'UI Designer',
                    'UX Researcher',
                    'Graphic Designer',
                    'Data Analyst',
                    'Data Scientist',
                    'Machine Learning Engineer',
                    'AI Engineer',
                    'Business Analyst',
                    'Financial Analyst',
                    'Data Engineer',
                    'Frontend Developer',
                    'Backend Developer',
                    'Full Stack Developer',
                    'Mobile Developer',
                    'iOS Developer',
                    'Android Developer',
                    'DevOps Engineer',
                    'Site Reliability Engineer',
                    'Cloud Engineer',
                    'Solutions Architect',
                    'Technical Architect',
                    'QA Engineer',
                    'Test Engineer',
                    'Security Engineer',
                    'Cybersecurity Analyst',
                    'Network Engineer',
                    'Systems Administrator',
                    'Database Administrator',
                    'Marketing Manager',
                    'Digital Marketing Manager',
                    'Content Marketing Manager',
                    'Social Media Manager',
                    'SEO Specialist',
                    'Sales Representative',
                    'Account Executive',
                    'Sales Manager',
                    'Business Development Manager',
                    'Customer Success Manager',
                    'Account Manager',
                    'Project Manager',
                    'Program Manager',
                    'Scrum Master',
                    'Agile Coach',
                    'Technical Writer',
                    'Content Writer',
                    'Copywriter',
                    'HR Manager',
                    'Recruiter',
                    'Operations Manager',
                    'Supply Chain Manager',
                    'Consultant',
                    'Strategy Consultant',
                ]}
                label="Desired Role or Title"
                required={true}
            />
                        <p className="text-xs text-gray-500 mt-2">Helps identify relevant hiring signals and job matches</p>
                </div>
            </div>

                {/* Right Column */}
                <div className="space-y-5">
            {/* Location */}
                    <div>
            <AutocompleteInput
                value={profileData.locationPreference}
                onChange={(e) => setProfileData({ ...profileData, locationPreference: e.target.value })}
                placeholder="e.g., San Francisco, CA or Remote"
                suggestions={[
                    'Remote',
                    'San Francisco, CA',
                    'New York, NY',
                    'Seattle, WA',
                    'Austin, TX',
                    'Boston, MA',
                    'Los Angeles, CA',
                    'Chicago, IL',
                    'Denver, CO',
                    'Atlanta, GA',
                    'Miami, FL',
                    'Portland, OR',
                    'San Diego, CA',
                    'Phoenix, AZ',
                    'Dallas, TX',
                    'Houston, TX',
                    'Philadelphia, PA',
                    'Washington, DC',
                    'Minneapolis, MN',
                    'Detroit, MI',
                    'Nashville, TN',
                    'Charlotte, NC',
                    'Raleigh, NC',
                    'Salt Lake City, UT',
                    'London, UK',
                    'Manchester, UK',
                    'Edinburgh, UK',
                    'Dublin, Ireland',
                    'Paris, France',
                    'Berlin, Germany',
                    'Munich, Germany',
                    'Amsterdam, Netherlands',
                    'Stockholm, Sweden',
                    'Copenhagen, Denmark',
                    'Oslo, Norway',
                    'Zurich, Switzerland',
                    'Barcelona, Spain',
                    'Madrid, Spain',
                    'Milan, Italy',
                    'Rome, Italy',
                    'Toronto, Canada',
                    'Vancouver, Canada',
                    'Montreal, Canada',
                    'Singapore',
                    'Hong Kong',
                    'Tokyo, Japan',
                    'Seoul, South Korea',
                    'Sydney, Australia',
                    'Melbourne, Australia',
                    'Auckland, New Zealand',
                    'Dubai, UAE',
                    'Tel Aviv, Israel',
                    'Bangalore, India',
                    'Mumbai, India',
                    'Delhi, India',
                ]}
                label="Location Preference"
                required={true}
            />
                        <p className="text-xs text-gray-500 mt-2">We'll prioritize opportunities in or near this location</p>
                    </div>

                    {/* Skills */}
                    <div>
                        <label className="block text-sm font-bold text-gray-900 mb-3">
                            Key Skills <span className="text-red-500">*</span>
                            <span className="text-gray-500 font-normal ml-2">(Min 3)</span>
                        </label>
                        <div className="flex gap-2 mb-3">
                            <div className="flex-1 relative">
                                <input
                                    type="text"
                                    value={skillInput}
                                    onChange={(e) => setSkillInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                                    placeholder="Type skill name"
                                    className="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base"
                                />
                                <ChevronDown 
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" 
                                    size={18} 
                                />
                                {skillInput && (
                                    <div className="absolute z-50 w-full mt-2 bg-white border-2 border-blue-200 rounded-xl shadow-2xl max-h-48 overflow-y-auto">
                                        {['JavaScript', 'Python', 'Java', 'C++', 'C#', 'PHP', 'Ruby', 'Go', 'Rust', 'Swift', 'Kotlin', 'TypeScript', 'React', 'Angular', 'Vue.js', 'Node.js', 'Express.js', 'Django', 'Flask', 'Spring Boot', 'ASP.NET', 'Laravel', 'Ruby on Rails', 'SQL', 'MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'Oracle', 'AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'Jenkins', 'Git', 'GitHub', 'GitLab', 'CI/CD', 'Terraform', 'Ansible', 'Linux', 'Unix', 'Bash', 'PowerShell', 'HTML', 'CSS', 'SASS', 'Tailwind CSS', 'Bootstrap', 'jQuery', 'REST API', 'GraphQL', 'Microservices', 'Agile', 'Scrum', 'JIRA', 'Confluence', 'Figma', 'Adobe XD', 'Sketch', 'Photoshop', 'Illustrator', 'UI/UX Design', 'Project Management', 'Product Management', 'Data Analysis', 'Data Science', 'Machine Learning', 'Deep Learning', 'TensorFlow', 'PyTorch', 'Pandas', 'NumPy', 'R', 'Tableau', 'Power BI', 'Excel', 'Statistics', 'A/B Testing', 'SEO', 'SEM', 'Google Analytics', 'Content Marketing', 'Social Media Marketing', 'Email Marketing', 'Copywriting', 'Sales', 'Customer Service', 'Communication', 'Leadership', 'Problem Solving', 'Critical Thinking', 'Time Management', 'Team Collaboration']
                                            .filter(s => s.toLowerCase().includes(skillInput.toLowerCase()))
                                            .slice(0, 15)
                                            .map((suggestion, idx) => (
                                                <button
                                                    key={idx}
                                                    type="button"
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        setSkillInput(suggestion);
                                                    }}
                                                    className="w-full px-4 py-2.5 text-left hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-700 font-medium first:rounded-t-xl last:rounded-b-xl text-sm"
                                                >
                                                    {suggestion}
                                                </button>
                                            ))
                                        }
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={addSkill}
                                type="button"
                                className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 hover:shadow-lg transition-all text-sm"
                            >
                                Add
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2 min-h-[60px]">
                            {profileData.skills.map((skill) => (
                                <span
                                    key={skill}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 text-blue-700 rounded-lg font-semibold text-sm h-fit"
                                >
                                    {skill}
                                    <button
                                        onClick={() => removeSkill(skill)}
                                        className="hover:text-blue-900 transition-colors"
                                        type="button"
                                    >
                                        <X size={16} />
                                    </button>
                                </span>
                            ))}
                        </div>
                        {profileData.skills.length < 3 && (
                            <p className="text-xs text-amber-600 mt-2 flex items-center gap-2 font-medium">
                                <Clock size={16} />
                                Add {3 - profileData.skills.length} more skill{3 - profileData.skills.length !== 1 ? 's' : ''} to continue
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
    
    const canProceed = profileData.experienceLevel && profileData.desiredRole && 
                      profileData.skills.length > 0 && profileData.locationPreference;
    
    const content = (
        <>
            <div className="fixed inset-0 bg-slate-100/35 backdrop-blur-md z-[99998]" />
            <div className="fixed inset-0 z-[99999] flex items-center justify-center pointer-events-none">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="relative bg-white rounded-xl shadow-lg border border-gray-200 p-6 max-w-4xl mx-4 pointer-events-auto max-h-[90vh] overflow-y-auto"
                >
                    {formContent}
                    <div className="flex items-center gap-3 justify-center mt-6 pt-5 border-t border-gray-100">
                        {onSkip && (
                            <button
                                onClick={onSkip}
                                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
                            >
                                Skip
                            </button>
                        )}
                        <button
                            onClick={onNext}
                            disabled={!canProceed}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                canProceed
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                            Continue
                            <ArrowRight size={16} />
                        </button>
                    </div>
                </motion.div>
            </div>
        </>
    );
    
    return typeof document !== 'undefined' 
        ? createPortal(content, document.body)
        : formContent;
};

export default ProfileRequiredStep;
